import {useDispatch} from 'react-redux'
import {
	useState,
	useRef,
	useEffect,
	useCallback
} from 'react'
import { 
	Line,
	Scatter
} from 'react-chartjs-2'
import HowTo from './HowTo'
import Webcam from "react-webcam"
import useCountDown from "react-countdown-hook"
import {capitalize} from '../../util/util'
import useCurrentUser from '../../hooks/useCurrentUser'
import useWindowSize from '../../hooks/useWindowSize'
import * as poseDetection from '@tensorflow-models/pose-detection'
import * as tf from '@tensorflow/tfjs-core'
// Register one of the TF.js backends.
import '@tensorflow/tfjs-backend-webgl'
import {
	getLegSide,
	isWebview,
	sideKneeAngle,
	confidentSide,
	normalizeX,
	repConsistency,
	tempoConsistency,
	depthScore,
	extractRepSlices,
	nonMonotonicIndexes,
	argMin,
	argMax,
	getKneeCaveTimeMap,
	restConsistency,
	kneeArticulationConsistency,
	centerLineConsistency,
	repsCenterLineScore,
	chestCenter,
	hipCenter,
	useAnimationFrame,
	poseFemurLength,
	poseHeight,
	balancingSide,
	calculateCenterStatus,
	calculateShoulderTilt,
	calculateHipTilt,
	tiltScore,
	kneeHeightScore,
	calculateThighLength,
	calculateCalfLength,
	heelHipDistanceEstimate,
	nearest
} from './util'
import {
	removeLeaderboardRecord,
	editLeaderboardRecord,
	addLeaderboardRecord,
	setMetricCreator,
	setPoses,
	setShow
} from './slice'
import styles from './AIDemo.module.css'
import Guides from './Guides'
import {dateToYMD} from '../../util/datetime'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Table from 'react-bootstrap/Table'
import Spinner from 'react-bootstrap/Spinner'
import Leaderboard from './Leaderboard'
import Poses from './Poses'
import KeypointLogViewer from './KeypointLogViewer'
import testData from './testKneeFlexionData.json'

const SCORE_THRESHOLD = 0.3

const MODEL_INPUT_SIZE = 512

const detectorConfig = {
	modelType: poseDetection.movenet.modelType.SINGLEPOSE_THUNDER,
	enableTracking: true,
	trackerType: poseDetection.TrackerType.BoundingBox,
	multiPoseMaxDimension:MODEL_INPUT_SIZE
}

const KneeFlexionDemo = ()=>{
	const dispatch = useDispatch()
	const [windowWidth,windowHeight] = useWindowSize()

	const user = useCurrentUser()
	const isClient = user.account_type === 'client'
	const ratio = 1//windowWidth/windowHeight

	const [detector,setDetector] = useState(null)
	const [showHowTo,setShowHowTo] = useState(false)
	const [showResults,setShowResults] = useState(false)
	const [showLeaderboard,setShowLeaderboard] = useState(false)
	const testVidRef = useRef(null)
	const testVidLoadedRef = useRef(false)

	const canvasRef = useRef(null)

	const leftPassiveDegreeSpanRef = useRef(null)
	const leftActiveDegreeSpanRef = useRef(null)

	const rightPassiveDegreeSpanRef = useRef(null)
	const rightActiveDegreeSpanRef = useRef(null)

	const refTree = {
		left:{
			passive:leftPassiveDegreeSpanRef,
			active:leftActiveDegreeSpanRef
		},
		right:{
			passive:rightPassiveDegreeSpanRef,
			active:rightActiveDegreeSpanRef
		}
	}

	//stores results from squat analysis
	const emptyResults = ()=>({
		keypointLog:[],
		left:{
			passive:0,
			active:0
		},
		right:{
			passive:0,
			active:0
		},
		//when readouts occured
		times:{
			left:{
				passive:0,
				active:0
			},
			right:{
				passive:0,
				active:0
			},
		}
	})

	const [results,setResults] = useState(testData)//useState(emptyResults())//
	const resultsRef = useRef(emptyResults())

	//console.log(results)

	const posesRef = useRef([])
	const [showGuides,setShowGuides] = useState(false)
	const [showOverlay,setShowOverlay] = useState(false)
	const overlayRef = useRef(null)
	
	const guidesRef = useRef({
		thighLength:60,
		calfLength:60
	})

	const [name,setName] = useState('')
	const [email,setEmail] = useState('')

	useEffect( async ()=>{
		setDetector(
			await poseDetection.createDetector(
				poseDetection.SupportedModels.MoveNet,
				detectorConfig
			)
		)
	},[])

	const [
		timeLeft, 
		{
			start:startCoundown,
			reset:resetCountdown,
			pause:pauseCountdown
		}
	] = useCountDown(10000, 1000)

	const [countdownType,setCountDownType] = useState('init')

	useEffect(()=>{
		if ( timeLeft === 0 ) {
			if(countdownType==='init'){
				setOverlay(`Loading Test`)
				setTimeout(startInferencing,2000)
			} else if(countdownType==='switching') {
				toggleIsSwitchingSides()
			}
		} else {
			if(countdownType==='init'){
				setOverlay(`Starting ${(timeLeft/1000).toFixed(0)}`)
			} else if(countdownType==='switching') {
				setOverlay(`Face right side to camera & Straighten Legs ${(timeLeft/1000).toFixed(0)}`)
			}
		}
	},[timeLeft])

	//console.log('render')
	//reference to if we have initialized the squat guides
	const initializedRef = useRef(false)
	const trackResultsRef = useRef(false)
	const webcamRef = useRef(null)

	const stateDisplayTextMap = {
		waiting:'Waiting',
		setup:'Get Ready',
		measure:'Measuring'
	}

	const initState = 'waiting'
	const stateRef = useRef(initState)
	const squatStateSpanRef = useRef(null)

	//stores which side we're measuring and which metric
	//eg left side active
	const isMeasuringRef = useRef(false)
	const isSwitchingSidesRef = useRef(false)

	const isSwitchingSides = ()=> isSwitchingSidesRef.current
	const toggleIsSwitchingSides = ()=> isSwitchingSidesRef.current = !isSwitchingSidesRef.current

	const requiredMeasurements = ()=>(
		[
			{
				side:'left',
				measurements:['active','passive']
			},{
				side:'right',
				measurements:['active','passive']
			}
		]
	)

	const requiredMeasurementsRef = useRef(requiredMeasurements())

	const setOverlay = (text)=>{
		overlayRef.current.innerText = text
	}

	//functions to use during each animation frame/inference
	const toggleMeasuring = ()=>{
		isMeasuringRef.current = !isMeasuringRef.current
	}

	const getRequiredMeasurement = (side) => requiredMeasurementsRef.current[0].measurements[0]

	const shiftRequiredSideMeasurements = ()=>{
		requiredMeasurementsRef.current[0].measurements = requiredMeasurementsRef.current[0].measurements.slice(1)
	}

	const shiftRequiredSide = ()=>{
		requiredMeasurementsRef.current = requiredMeasurementsRef.current.slice(1)
	}

	const getMeasurement = (side,metric) => resultsRef.current[side][metric]

	const getSide = () => requiredMeasurementsRef.current[0].side
	
	//predicates

	const isMeasuring = ()=> isMeasuringRef.current

	const legIsStraight = (side,keypoints)=>{
		const angle = sideKneeAngle(side,keypoints)
		const legStraight = angle >= 170
		//ensure horizontal 
		const hip = keypoints[ side === 'left' ? 11 : 12 ]
		const knee = keypoints[ side === 'left' ? 13 : 14 ]
		const ankle = keypoints[ side === 'left' ? 15 : 16 ]
		const maxY = Math.max(hip.y,knee.y,ankle.y)
		const minY = Math.min(hip.y,knee.y,ankle.y)
		const delta = maxY - minY
		return legStraight && delta <= 25
	}

	const hasAllMeasurementsForSide = (side)=>{
		return hasMeasurement(side,'passive') && hasMeasurement(side,'active')
	}

	const armIsBehindBack = (side,keypoints)=>{
		const isLeft = side === 'left'
		const hip = keypoints[isLeft?11:12]
		const elbow = keypoints[isLeft?7:8]
		const wrist = keypoints[isLeft?9:10]

		const armBehindBack = (
			isLeft 
			? (elbow.x > hip.x && wrist.x > hip.x)
			: (elbow.x < hip.x && wrist.x < hip.x)
		)

		return armBehindBack
	}

	const setMeasurment = (side,measurement,angle,t)=>{
		const prevAngle = getMeasurement(side,measurement)
		const newAngle = (
			hasMeasurement(side,measurement)
			? Math.max(prevAngle,angle)
			: angle
		)
		//set timestamp of new reading
		if(prevAngle!==newAngle){
			resultsRef.current.times[side][measurement] = t
		}
		resultsRef.current[side][measurement] = newAngle
	}

	const updateMeasurementDisplay = (side,measurement)=>{
		refTree[side][measurement].current.innerText = getMeasurement(side,measurement).toFixed(1)
	}

	const hasAllMeasurmentsAboveThreshold = (threshold)=>(
		hasMeasurement('left','active') && getMeasurement('left','active') >= threshold &&
		hasMeasurement('left','passive') && getMeasurement('left','passive') >= threshold &&
		hasMeasurement('right','active') && getMeasurement('right','active') >= threshold &&
		hasMeasurement('right','passive') && getMeasurement('right','passive') >= threshold
	)

	const hasMeasurement = (side,measurement) => Boolean(getMeasurement(side,measurement))

	const hasStableReadingGreaterThanThreshold = (side,measurement,threshold,t)=>{
		//if no change has occured on the readout for the specified time switch
		const prevTime = resultsRef.current.times[side][measurement]
		const stableTime = 2000
		const isStable = t - prevTime > stableTime
		return hasMeasurement(side,measurement) && isStable && getMeasurement(side,measurement) >= threshold
	}

	const poseIsCentered = keypoints => confidentSide(keypoints) === 'front'

	const isReadyPose = keypoints => (
		!poseIsCentered(keypoints) && 
		armIsBehindBack(getSide(),keypoints) &&
		legIsStraight(getSide(),keypoints) &&
		//the leg being tested should be pointing the opposite direction
		//eg left leg should be pointing right 
		getLegSide(getSide(),keypoints) !== getSide()
	)

	const infer = useCallback( async () => {
		if ((!webcamRef.current&&!testVidRef.current)||!detector) {
			//console.log('Waiting for dependencies')
		} else {
			const video = webcamRef.current?.video || testVidRef.current

			const ctx = canvasRef.current.getContext("2d")
			ctx.drawImage(video,0,0,MODEL_INPUT_SIZE,MODEL_INPUT_SIZE/ratio)
			if (!initializedRef.current||!trackResultsRef.current) {
				console.log('Not initialized')
				return
			}

			try {
				//console.log(`inferencing`)
				const poses = await detector.estimatePoses(canvasRef.current)
				if (poses.length===0) return
				const {keypoints} = poses[0]
				const side = getSide()

				//if we have all measurements below a threshold and the person has 
				//returned back to ready pose then proceed to results display
				if (isMeasuring()&&!poseIsCentered(keypoints)&&!isSwitchingSides()) {
					const t = Date.now()
					resultsRef.current.keypointLog.push({keypoints,t})
					const measurement = getRequiredMeasurement(side)
					//in sideKneeAngle the angle is measured using the inside angle 
					//though a physical therapist measures it as 180 - this value
					const angle = 180 - sideKneeAngle(side,keypoints)
					
					setMeasurment(side,measurement,angle,t)
					updateMeasurementDisplay(side,measurement)

					if (hasStableReadingGreaterThanThreshold(side,measurement,120,t)) { 
						if (hasAllMeasurmentsAboveThreshold(100)) {
							setOverlay('Done. Processing Results')
							setTimeout(()=>{
								cancelInferencing()
								setShowResults(true)
								setResults(resultsRef.current)
								setShowOverlay(false)
							},2000)
						} else if (hasAllMeasurementsForSide(side)) {
							shiftRequiredSide()
						} else {
							shiftRequiredSideMeasurements(side)
						}
						toggleMeasuring()
					}
				}

				if (!isMeasuring()&&!hasAllMeasurmentsAboveThreshold(100)&&!isSwitchingSides()) {
					if (isReadyPose(keypoints)) {
						//set guide lengths
						guidesRef.current.calfLength = calculateCalfLength(keypoints,side)
						guidesRef.current.thighLength = calculateThighLength(keypoints,side)
						toggleMeasuring()
						const measurement = getRequiredMeasurement(side)
						setOverlay(
							`Bend Knee ${measurement==='active'?'Unassisted':'Assisted'}`
						)
					} else {
						const measurement = getRequiredMeasurement(side)
						const message = (
							measurement === 'passive'
							? 'Straighten Legs'
							: `Face ${side} side to camera & Straighten Legs${side==='right'?' 10':''}`
						)
						setOverlay(message)
						//set a timer for switching sides
						if ( side === 'right' && measurement === 'active' ) {
							toggleIsSwitchingSides()
							setCountDownType('switching')
							startCoundown()
						}
					}
				}

			} catch(err) {
				console.error(err)
			}
		}
	},[detector,webcamRef,guidesRef])

	const {
		cancel:cancelInferencing,
		start:startInferencing
	} = useAnimationFrame(infer)

	const timeoutRef = useRef()
	const [started,setStarted] = useState(false)

	//Goes through a series of onscreen instructions then
	//calls the onEnd callback when finished
	const startSetupInstructionsOverlay = onEnd => {
		const instructions = [
			'How to do this test:',
			'Your Left leg will be tested first',
			'During the countdown step back & sit',
			'Face your left side to the camera',
			'Ensure your full body is in view',
			'Follow the onscreen instructions',
			'Starting countdown'
		]

		const nextInstruction = i => {
			setShowOverlay(true)
			setOverlay(instructions[i])
			if ( i >= instructions.length ){ 
				onEnd()
			} else {
				timeoutRef.current = setTimeout(nextInstruction.bind(null,i+1),5000)
			}
		}
		
		nextInstruction(0)

	}

	const startDemo = async ()=>{
		requiredMeasurementsRef.current = requiredMeasurements()
		setStarted(true)
		cancelInferencing()
		setShowGuides(false)
		setShowOverlay(true)
		startSetupInstructionsOverlay(startCoundown)
		setCountDownType('init')
		resultsRef.current = emptyResults()
		squatStateSpanRef.current.innerText = stateDisplayTextMap['setup']

		initializedRef.current = true
		stateRef.current = 'setup'
		resultsRef.current = emptyResults()
		trackResultsRef.current = true
		if (testVidRef.current) testVidRef.current.play()
		
		//run a single inference to load model unto GPU
		const ctx = canvasRef.current.getContext("2d")
		if (testVidRef.current) {
			ctx.drawImage(testVidRef.current,0,0,MODEL_INPUT_SIZE,MODEL_INPUT_SIZE/ratio)
		} else {
			ctx.drawImage(webcamRef.current.video,0,0,MODEL_INPUT_SIZE,MODEL_INPUT_SIZE/ratio)
		}
		const poses = await detector.estimatePoses(canvasRef.current)
	}

	const restartDemo = ()=>{
		setShowResults(false)
		setShowGuides(false)
		cancelInferencing()
		setStarted(false)
		requiredMeasurementsRef.current = requiredMeasurements()
		resultsRef.current = emptyResults()
	}

	const stopDemo = ()=>{
		pauseCountdown()
		setShowOverlay(false)
		setShowGuides(false)
		cancelInferencing()
		setStarted(false)
		resultsRef.current = emptyResults()
		requiredMeasurementsRef.current = requiredMeasurements()
		squatStateSpanRef.current.innerText = stateDisplayTextMap[initState]
		if (testVidRef.current) {
			testVidRef.current.pause()
			testVidRef.current.currentTime = 0
		}
		updateMeasurementDisplay('left','active')
		updateMeasurementDisplay('left','passive')
		updateMeasurementDisplay('right','active')
		updateMeasurementDisplay('right','passive')
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
		}
	}

	const handleSkipInstructions = ()=>{
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
		}
		startCoundown()
	}

	const handleStartTracking = ()=>{
		resultsRef.current = emptyResults()
		trackResultsRef.current=true
		startInferencing()
	}

	const handleReset = ()=>{
		cancelInferencing()
	}

	const [saving,setSaving] = useState(false)

	const handleSave = ()=>{
		setSaving(true)
		dispatch(addLeaderboardRecord({
			contact_email:email,
			rep_pace:repConsistency(results.reps),
			rest_pace:restConsistency(results.reps),
			depth_score:depthScore(
				results.reps,
				results.chestPosition,
				guidesRef.current.bottomY,
				guidesRef.current.topY
			),
			center_balance:centerLineConsistency(results.chestPosition),
			left_knee_balance:kneeArticulationConsistency(
				results.reps,
				results.leftKneePosition,
				results.kneeCaveMap.left
			),
			right_knee_balance:kneeArticulationConsistency(
				results.reps,
				results.rightKneePosition,
				results.kneeCaveMap.right
			),
			name:name,
		})).then(()=>{
			setSaving(false)
			setName('')
			setEmail('')
		})
	}

	const handleAddToProgress = ()=>{

		const metrics = []
		const sides = ['left','right']
		const measurements = ['active','passive']

		sides.forEach( side => {
			measurements.forEach( measurement => {
				metrics.push({
					name:`${capitalize(side)} ${capitalize(measurement)} Flexion`,
					description:'',
					//implicit
					unit_id:1,
					value:results[side][measurement].toFixed(2)
				})
			})
		})

		dispatch(setMetricCreator({
			show:true,
			category:'AI Knee Flexion Measurements',
			date:dateToYMD(new Date()),
			metrics,
			aiLog:JSON.stringify(results)
		}))
	}

	const averageScore = (side)=>{
		const scores = [
			repConsistency(results[side].reps),
			depthScore(
				results[side].reps,
				results[side].chestPosition,
				guidesRef.current.squatBottomY,
				guidesRef.current.topY
			),
			restConsistency(results[side].reps),
			centerLineConsistency(results[side].chestPosition),
			kneeArticulationConsistency(
				results[side].reps,
				results[side].leftKneePosition,
				results[side].kneeCaveMap[side]
			),
			tiltScore(
				results[side].hipTilt.map(p=>({
					x:p.t - results[side].hipTilt[0].t,
					y:p.angle,
					status:p.status
				}))
			)
		]
		const average = scores.reduce(
			(carry,score)=>carry+score,
			0
		)/scores.length
		return average
	}

	return (
		<div className={styles.AIDemoWidget}>
			<div ref={overlayRef} style={{
				position:'absolute',
				width:'100vw',
				height:'100vh',
				padding:'5%',
				background:'rgba(0,0,0,0.4)',
				color:'rgb(50,255,50)',
				display:showOverlay?'flex':'none',
				justifyContent:'center',
				fontWeight:600,
				alignItems:'center',
				fontSize:windowWidth>1000?'100px':'50px',
				textAlign:'center',
				zIndex:3
			}}/>
			{!showResults&&
				<div className="m-auto">
					<div style={{position:'relative'}}>
						
						<svg style={{
							position:'absolute',
							top:0,left:0,
							zIndex:2,
							overflow:'visible',
							height:`${Math.min(windowWidth,windowHeight)}px`,
							width:`${Math.min(windowWidth,windowHeight)}px`,
						}}>
							
						</svg>
					</div>
					
					{/*<video 
						style={{
							height:`${Math.min(windowWidth,windowHeight)}px`,
							width:`${Math.min(windowWidth,windowHeight)}px`,
							display:'block'
						}}
						onLoadedData={()=>{testVidLoadedRef.current = true}}
						crossorigin='anonymous' 
						ref={testVidRef} 
						muted loop
						src="http://localhost/assets/videos/flexion-crop.mp4"
					/>*/}
					<Webcam 
						style={{
							height:windowWidth>windowHeight?'100vh':'auto',
							width:windowWidth>=windowHeight?'auto':'100vw',
							display:'block',
							margin:'auto'
						}}
						ref={webcamRef}
						mirrored
						videoConstraints={{
							width: 720,
							height:720,
							facingMode:"user",
							deviceId:"c141e16a643707300abde9eaae8a522f531793da15be147c3e1d7f40dcdfaea7"
						}}
					/>
					<canvas 
						style={{display:'none'}} 
						width={MODEL_INPUT_SIZE} height={MODEL_INPUT_SIZE} 
						ref={canvasRef}
					/>
					<div style={{
						position:'absolute',
						top:'4px',
						right:'4px',
						background:'rgba(255,255,255,0.9)',
						padding:'8px',
						borderRadius:'8px',
						zIndex:3,
						width:'200px'
					}}>
						<div className="d-flex align-items-stretch justify-content-around mb-2" style={{fontSize:'16px'}}>
							<div>
								<div>
									Act: <span 
										style={{width:'40px',textAlign:'right',display:'inline-block'}} 
										ref={leftActiveDegreeSpanRef}>0</span>&#xb0;
								</div>
								<div>
									Psv: <span 
										style={{width:'40px',textAlign:'right',display:'inline-block'}} 
										ref={leftPassiveDegreeSpanRef}>0</span>&#xb0;
								</div>
								<small style={{fontSize:'14px',margin:0}} className="text-muted d-block text-center">Left</small>
							</div>
							<span style={{background:'#ccc',width:'1px'}}/>
							<div>
								<div>
									Act: <span 
										style={{width:'40px',textAlign:'right',display:'inline-block'}} 
										ref={rightActiveDegreeSpanRef}>0</span>&#xb0;
								</div>
								<div>
									Psv: <span 
										style={{width:'40px',textAlign:'right',display:'inline-block'}} 
										ref={rightPassiveDegreeSpanRef}>0</span>&#xb0;
								</div>
								<small style={{fontSize:'14px',margin:0}} className="text-muted d-block text-center">Right</small>
							</div>
						</div>
						<div className="d-flex justify-content-between">
							<small className="mr-2" ref={squatStateSpanRef}>Waiting</small>
							<small className={Boolean(detector)?'text-success':'text-muted'}>
								{Boolean(detector)?'AI Downloaded':'Downloading AI...'}
							</small>
						</div>
					</div>
					<div style={{
						position:'absolute',
						bottom:0,
						left:0,
						display:'flex',
						flexWrap:'wrap',
						background:'rgba(255,255,255,0.9)',
						padding:'8px',
						zIndex:3
					}}>
						<Button
							className="mr-2"
							variant="outline-primary"
							onClick={()=>{
								setShowHowTo(p=>!p)
							}}
						>
							<i className="fa fa-question-circle" /> How To
						</Button>
						{!started&&
							<Button className="mr-2" onClick={startDemo} variant="outline-success">
								Start
							</Button>
						}
						{started&&
							<Button 
								variant="outline-secondary"
								className="mr-2"
								onClick={handleSkipInstructions}
							>
								Skip Instructions
							</Button>
						}
						{started&&
							<Button className="mr-2" onClick={stopDemo} variant="outline-danger">
								Stop
							</Button>
						}
						{Boolean(testVidRef.current)&&
							<Button className="mr-2" onClick={()=>{
								testVidRef.current.pause()
							}} variant="outline-info">
								Pause
							</Button>
						}
						{!isWebview()&&
							<Button 
								className="mr-2"
								onClick={()=>{
									dispatch(setShow(false))
								}}
								variant="outline-danger" 
							>
								Close
							</Button>
						}
					</div>
				</div>
			}
			{showResults&&
				<div style={{
					width:'100vw',
					height:'100vh',
					overflow:'auto',
					padding:'16px'
				}}>
					<div style={{
						fontSize:'32px',
						textAlign:'center',
						marginTop:'44px',
						marginBottom:'16px'
					}}>
						Results Analysis
					</div>
					<div>
						<Table striped bordered style={{background:'#fff'}}>
							<thead>
								<tr>
									<th>Metric</th>
									<th>Angle</th>
									<th>Gap Size (inches)</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td>Left Active Flexion</td>
									<td>{results.left.active.toFixed(1)}&#xb0;</td>
									<td>{heelHipDistanceEstimate(guidesRef.current.thighLength,guidesRef.current.calfLength,180 - results.left.active).toFixed(1)}"</td>
								</tr>
								<tr>
									<td>Left Passive Flexion</td>
									<td>{results.left.passive.toFixed(1)}&#xb0;</td>
									<td>{heelHipDistanceEstimate(guidesRef.current.thighLength,guidesRef.current.calfLength,180 - results.left.passive).toFixed(1)}"</td>
								</tr>
								<tr>
									<td>Right Active Flexion</td>
									<td>{results.right.active.toFixed(1)}&#xb0;</td>
									<td>{heelHipDistanceEstimate(guidesRef.current.thighLength,guidesRef.current.calfLength,180 - results.right.active).toFixed(1)}"</td>
								</tr>
								<tr>
									<td>Right Passive Flexion</td>
									<td>{results.right.passive.toFixed(1)}&#xb0;</td>
									<td>{heelHipDistanceEstimate(guidesRef.current.thighLength,guidesRef.current.calfLength,180 - results.right.passive).toFixed(1)}"</td>
								</tr>
							</tbody>
						</Table>

						<KeypointLogViewer 
							demo="knee-flexion"
							id="knee-flexion"
							results={results} 
							size={512}
						/>
					</div>
					<Button 
						onClick={handleAddToProgress}
						variant="outline-success" 
						className="my-3"
						block
					>
						Add to {isClient?'':"Client's"} Progress Metrics
					</Button>
					<div className="mt-5 d-flex">
						<Button 
							className="mr-2"
							onClick={restartDemo}
							variant="outline-dark"
						>
							Restart
						</Button>
						{!isWebview()&&
							<Button 
								onClick={()=>{
									dispatch(setShow(false))
								}}
								variant="outline-danger" 
							>
								Close
							</Button>
						}
					</div>
				</div>
			}
			{(parseInt(user.user_id_number) === 100017099 || window.location.hostname === 'localhost')&&
				<div style={{
					position:'absolute',
					top:0,left:0,
					transform:`translateX(${showLeaderboard?'0':'-100%'})`, 
					transition:'0.2s',
					width:'600px',
					maxWidth:'100%',
					height:'100vh',
					zIndex:6,
					boxShadow:showLeaderboard?'0 0 16px rgba(0,0,0,0.6)':'none'
				}}>
					<button 
						onClick={()=>{
							setShowLeaderboard( p => !p )
						}}
						style={{
							position:'absolute',
							top:'80px',right:0,
							transform:'translateX(100%)',
							padding:'16px',
							background:'#fff',
							border:'none',
							zIndex:5,
							boxShadow:'0 0 16px rgba(0,0,0,0.2)',
						}}
					>
						Leaderboard
					</button>
					<div style={{
						width:'100%',
						height:'100%',
						overflow:'auto',
						position:'absolute',
						background:'#fff',
						padding:'8px',
						top:0,left:0,
						zIndex:6
					}}>
						<Leaderboard/>
					</div>
				</div>
			}
			{showHowTo&&
				<HowTo onHide={
					()=>{setShowHowTo(false)}
				}>
					<strong>What is this?</strong>
					<p>
						With only a webcam, our system will automatically measure the angle of your active and passive knee flexion.
					</p>
					<strong>How do I use this?</strong>
					<ul>
						<li><span className="mr-2">1.</span>Press the green <span id={styles.StartHowTo}>Start</span> button & <strong>wait</strong> till it turns into a red <span id={styles.StopHowTo}>Stop</span> button (the first time always takes a few seconds)</li>
						<li><span className="mr-2">2.</span>Sit sideways with your <strong>full body in view</strong> of the camera.</li>
						<li><span className="mr-2">3.</span>Get into "ready pose:" <strong>both legs straight and both hands on the ground behind you</strong>.</li>
						<li><span className="mr-2">4.</span>Without the assistance of your arms bring foot as close to your body as you can and rest it on the ground.</li>
						<li><span className="mr-2">5.</span><strong>Wait for the system to tell you to get back into ready pose for next measurement (typically a few seconds) and repeat</strong> this process using your arms to assist.</li>
						<li><span className="mr-2">6.</span>Rotate so that your other side is facing the camera and repeat steps 2-5</li>
					</ul>
					<div className="d-flex align-items-end flex-wrap justify-content-around">
						<a target="_blank" href="https://www.youtube.com/watch?v=rPjhkwmExEY">Check out a live demo</a>
						<div className="text-center">
							<div><small>For more more info read the</small></div>
							<a target="_blank" href="https://www.fitsw.com/blog/exercise-motion-analysis-ai/">Blog Post About fitsw.ai</a>
						</div>
					</div>
				</HowTo>
			}
		</div>
	)
}

export default KneeFlexionDemo